import {
  CharterAdministratorHistoryDto,
  CharterCapitalHistoryDto,
  CharterHistoryEvent,
  CharterHolderHistoryDto,
  CharterSourceDto,
  CharterValueSource,
} from '@interfaces/corporate-charter/responses/corporate-charter.response';
import {
  GOVERNANCE_BODY_LABEL,
  formatMoney,
  formatPercent,
  formatUnits,
} from '@features/dashboard/company/corporate-charter/corporate-charter.util';

/**
 * Procedência de uma informação, no formato que a tela consome. `CharterValueDto`, `CharterSourceDto`, administrador,
 * regra de assinatura, cláusula e mudança usam os mesmos nomes de campo (ato, documento, data do ato, página e
 * trecho), então qualquer um deles serve de entrada para `app-charter-provenance`.
 */
export interface CharterProvenance {
  actId?: string | null;
  actLabel?: string | null;
  documentId?: string | null;
  documentName?: string | null;
  actDate?: string | null;
  page?: number | null;
  quote?: string | null;
  source?: CharterValueSource | null;
}

/** Pedido para abrir o documento original de um valor na página onde ele está escrito. */
export interface CharterDocumentRequest {
  documentId: string;
  documentName: string | null;
  actLabel: string | null;
  actDate: string | null;
  page: number | null;
  quote: string | null;
}

/** A procedência aponta para um documento do Data Room que dá para abrir? */
export function canOpenProvenance(source: CharterProvenance | null | undefined): boolean {
  return !!source?.documentId;
}

/** A procedência tem algo para mostrar (ato, página ou trecho)? */
export function hasProvenance(source: CharterProvenance | null | undefined): boolean {
  return !!source && (!!source.actLabel || !!source.page || !!source.quote || !!source.documentId);
}

export function toDocumentRequest(source: CharterProvenance): CharterDocumentRequest | null {
  if (!source.documentId) {
    return null;
  }
  return {
    documentId: source.documentId,
    documentName: source.documentName ?? null,
    actLabel: source.actLabel ?? null,
    actDate: source.actDate ?? null,
    page: source.page ?? null,
    quote: source.quote ?? null,
  };
}

/**
 * Trecho curto para buscar no PDF: as primeiras palavras do trecho literal. O trecho inteiro costuma atravessar quebra
 * de linha e hifenização, e a busca do pdf.js não acharia.
 */
export function quoteSearchText(quote: string | null | undefined, words = 7): string | null {
  if (!quote) {
    return null;
  }
  const clean = quote.replace(/["“”]/g, '').replace(/\s+/g, ' ').trim();
  const parts = clean.split(' ').filter(Boolean);
  return parts.length === 0 ? null : parts.slice(0, words).join(' ');
}

/** Rótulo de cada evento do histórico. */
export const HISTORY_EVENT_LABEL: Record<CharterHistoryEvent, string> = {
  listed: 'consta no ato',
  'holder-entry': 'entrou',
  'holder-exit': 'saiu',
  'units-transfer': 'cessão de quotas',
  'capital-increase': 'aumento de capital',
  'capital-decrease': 'redução de capital',
  'capital-payment': 'integralização',
  'share-class-change': 'mudança de classe',
  'administrator-appointed': 'nomeado',
  'administrator-removed': 'destituído',
  'board-elected': 'eleito para o conselho',
  'executive-board-elected': 'eleito para a diretoria',
  'signing-rules-changed': 'regras de assinatura',
  'corporate-name-changed': 'razão social',
  'head-office-changed': 'sede',
  'purpose-changed': 'objeto social',
  'legal-nature-changed': 'tipo societário',
  'clause-changed': 'cláusula alterada',
  'bylaws-consolidated': 'consolidação',
  other: 'mudança',
};

export type CharterHistoryTone = 'listed' | 'entry' | 'exit' | 'change';

const EXIT_EVENTS = new Set<CharterHistoryEvent>(['holder-exit', 'administrator-removed']);
const ENTRY_EVENTS = new Set<CharterHistoryEvent>([
  'holder-entry',
  'administrator-appointed',
  'board-elected',
  'executive-board-elected',
]);

/** Linha da linha do tempo de um sócio, administrador ou do capital. */
export interface CharterHistoryRow {
  provenance: CharterSourceDto;
  eventLabel: string;
  tone: CharterHistoryTone;
  /** Valores do ato: "4.000 quotas · 40% · R$ 4.000,00". */
  values: string | null;
  summary: string | null;
}

function eventLabel(event: CharterHistoryEvent | null): string {
  return event ? (HISTORY_EVENT_LABEL[event] ?? event) : 'consta no ato';
}

function eventTone(event: CharterHistoryEvent | null): CharterHistoryTone {
  if (!event || event === 'listed') {
    return 'listed';
  }
  if (EXIT_EVENTS.has(event)) {
    return 'exit';
  }
  return ENTRY_EVENTS.has(event) ? 'entry' : 'change';
}

function joinValues(parts: (string | null)[]): string | null {
  const filled = parts.filter((part): part is string => !!part);
  return filled.length > 0 ? filled.join(' · ') : null;
}

/** Histórico de um sócio: quotas, participação e capital em cada ato, mais as mudanças que o envolvem. */
export function holderHistoryRows(
  history: CharterHolderHistoryDto[],
  unitKind: 'quotas' | 'shares',
  currency: string
): CharterHistoryRow[] {
  const noun = unitKind === 'shares' ? 'ações' : 'quotas';
  return history.map(item => ({
    provenance: item.provenance,
    eventLabel: eventLabel(item.event),
    tone: eventTone(item.event),
    values: joinValues([
      item.units != null ? `${formatUnits(item.units)} ${noun}` : null,
      item.percentage != null ? formatPercent(item.percentage) : null,
      item.capitalAmount != null ? formatMoney(item.capitalAmount, currency) : null,
    ]),
    summary: item.summary,
  }));
}

/** Histórico de um administrador: órgão, cargo e mandato em cada ato. */
export function administratorHistoryRows(
  history: CharterAdministratorHistoryDto[],
  formatDate: (value: string) => string
): CharterHistoryRow[] {
  return history.map(item => ({
    provenance: item.provenance,
    eventLabel: eventLabel(item.event),
    tone: eventTone(item.event),
    values: joinValues([
      item.body ? GOVERNANCE_BODY_LABEL[item.body] : null,
      item.role,
      item.termEnd ? `mandato até ${formatDate(item.termEnd)}` : null,
    ]),
    summary: null,
  }));
}

/** Histórico do capital: capital, integralizado, quotas ou ações e capital autorizado em cada ato. */
export function capitalHistoryRows(
  history: CharterCapitalHistoryDto[],
  unitKind: 'quotas' | 'shares',
  currency: string
): CharterHistoryRow[] {
  const noun = unitKind === 'shares' ? 'ações' : 'quotas';
  return history.map(item => ({
    provenance: item.provenance,
    eventLabel: 'capital no ato',
    tone: 'listed' as const,
    values: joinValues([
      item.shareCapital != null ? formatMoney(item.shareCapital, currency) : null,
      item.totalUnits != null ? `${formatUnits(item.totalUnits)} ${noun}` : null,
      item.unitValue != null ? `de ${formatMoney(item.unitValue, currency)}` : null,
      item.paidInCapital != null && item.paidInCapital !== item.shareCapital
        ? `${formatMoney(item.paidInCapital, currency)} integralizado`
        : null,
      item.authorizedCapital != null
        ? `autorizado até ${formatMoney(item.authorizedCapital, currency)}`
        : null,
    ]),
    summary: null,
  }));
}

/** Última mudança de saída do histórico ("saiu na 9ª alteração"); sem ela, o último ato que listava a pessoa. */
export function lastSeen(
  history: { provenance: CharterSourceDto; event: CharterHistoryEvent | null }[]
): { exited: boolean; provenance: CharterSourceDto } | null {
  const exit = [...history].reverse().find(item => item.event && EXIT_EVENTS.has(item.event));
  if (exit) {
    return { exited: true, provenance: exit.provenance };
  }
  const last = history[history.length - 1];
  return last ? { exited: false, provenance: last.provenance } : null;
}

/**
 * Fonte dominante de um cartão: o ato que mais aparece entre as procedências dos valores. O cabeçalho do cartão
 * mostra essa fonte uma vez, e só os valores vindos de outro ato ganham o selo "alterado na <ato>".
 */
export function dominantSource(
  items: (CharterProvenance | null | undefined)[]
): CharterProvenance | null {
  const counts = new Map<string, { count: number; source: CharterProvenance }>();
  for (const item of items) {
    if (!item?.actId) {
      continue;
    }
    const entry = counts.get(item.actId);
    if (entry) {
      entry.count++;
    } else {
      counts.set(item.actId, { count: 1, source: item });
    }
  }
  let best: { count: number; source: CharterProvenance } | null = null;
  for (const entry of counts.values()) {
    if (!best || entry.count > best.count) {
      best = entry;
    }
  }
  if (!best) {
    return null;
  }
  const s = best.source;
  return {
    actId: s.actId,
    actLabel: s.actLabel,
    documentId: s.documentId,
    documentName: s.documentName,
    actDate: s.actDate,
    page: null,
    quote: null,
  };
}

/**
 * O valor veio de um ato posterior à fonte do cartão (outro ato e data mais nova; sem data, basta ser outro ato).
 * Valor de ato anterior à fonte não é "alteração": só ficou igual desde então.
 */
export function isLaterThanBase(
  value: CharterProvenance | null | undefined,
  base: CharterProvenance | null | undefined
): boolean {
  if (!value?.actId || !base?.actId || value.actId === base.actId) {
    return false;
  }
  if (value.actDate && base.actDate) {
    return value.actDate > base.actDate;
  }
  return true;
}
